import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Label } from '../../components/widgets';
import AutoComplete from '../../components/molecules/AutoComplete';
import CollapsedPanelValues from '../../components/widgets/CollapsedPanelValues';
import AddFutureItems from '../containers/AddFutureItems';
import { buildSdkEventDisplayNameCounts, getSdkEventLabel, translationPrefix } from '../utils';
import css from './SdkEventStepCard.css';
import StepCard from './StepCard';

const getUniqueEventNames = events => [...new Set(events.map(e => e.eventName))];

// Match on both the (disambiguated) label and the raw event name
// The label already carries the event name for look-alikes; this
// also lets unique events be found by their raw name.
const filterEventOption = (option, rawInput) => {
    if (!rawInput) return true;
    const input = rawInput.toLowerCase();
    return [option.label, option.value].some(field =>
        String(field)
            .toLowerCase()
            .includes(input)
    );
};

const getDropdownOptions = (events, selectedEventNames, displayNameCounts) => {
    // Get unique events not already selected
    const unselectedEvents = events.filter(event => !selectedEventNames.includes(event.eventName));

    // Create a map to deduplicate by event name (in case backend sends duplicates)
    const eventsMap = new Map();
    unselectedEvents.forEach(event => {
        if (!eventsMap.has(event.eventName)) {
            eventsMap.set(event.eventName, {
                value: event.eventName,
                label: getSdkEventLabel(event, displayNameCounts),
            });
        }
    });

    // Return flat sorted list (no grouping)
    return Array.from(eventsMap.values()).sort((a, b) => a.label.localeCompare(b.label));
};

const SdkEventStepCard = ({
    events = [],
    selectedEventNames = [],
    onChange = () => {},
    onToggleAddFuture = () => {},
    translate = () => {},
    loading = false,
    activeMemberType = '',
}) => {
    const uniqueEventNames = useMemo(() => getUniqueEventNames(events), [events]);
    const selectedEventsCount = selectedEventNames.length;
    const totalEventsCount = uniqueEventNames.length;
    const isLoading = loading && events.length === 0;
    const hasNoEvents = !loading && events.length === 0;
    const noEventsMessageKey = `${translationPrefix}.MANAGE_PERMISSIONS_PAGE.NO_SDK_EVENTS_${activeMemberType.toUpperCase()}`;

    // Count colliding display names once, then look events up by name in O(1).
    const displayNameCounts = useMemo(() => buildSdkEventDisplayNameCounts(events), [events]);
    const eventsByName = useMemo(() => {
        const map = new Map();
        events.forEach(event => {
            if (!map.has(event.eventName)) map.set(event.eventName, event);
        });
        return map;
    }, [events]);

    const dropdownOptions = useMemo(() => getDropdownOptions(events, selectedEventNames, displayNameCounts), [
        events,
        selectedEventNames,
        displayNameCounts,
    ]);

    // Disambiguated labels for the selected chips, parallel to selectedEventNames.
    // The chips are keyed/removed by eventName (unique), not the label (which can
    // collide with a stale selection), so the right event is always removed.
    const selectedLabels = useMemo(
        () =>
            selectedEventNames.map(name => {
                const event = eventsByName.get(name);
                return event ? getSdkEventLabel(event, displayNameCounts) : name;
            }),
        [eventsByName, selectedEventNames, displayNameCounts]
    );

    const handleEventSelect = selectedOption => {
        if (selectedOption && selectedOption.value) {
            onChange([...selectedEventNames, selectedOption.value]);
        }
    };

    const handleRemoveEvent = eventName => onChange(selectedEventNames.filter(name => name !== eventName));

    const handleSelectAll = () => {
        onChange(uniqueEventNames);
        onToggleAddFuture(true);
    };

    const handleClearAll = () => onChange([]);

    return (
        <div className={classNames(css.container, { [css.emptyContainer]: hasNoEvents })}>
            <StepCard
                className={css.stepCard}
                contentClassName={css.stepCardContent}
                title={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.TITLE.SDK_EVENTS`}
                titleTextReplace={{
                    count: selectedEventsCount,
                    total: totalEventsCount,
                }}
                description={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.DESCRIPTION.${activeMemberType}.SDK_EVENTS`}
            >
                {isLoading ? (
                    <div className={css.emptyState}>
                        <Label type="p" text={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.LOADING_SDK_EVENTS`} />
                    </div>
                ) : hasNoEvents ? (
                    <div className={css.emptyState}>
                        <Label type="p" text={noEventsMessageKey} />
                    </div>
                ) : (
                    <>
                        <div className={css.dropdownContainer}>
                            <AutoComplete
                                placeholder={translate(`${translationPrefix}.PLACEHOLDER.SDK_EVENTS_SEARCH`)}
                                selectOptions={{
                                    options: dropdownOptions,
                                    filterOption: filterEventOption,
                                    noOptionsMessage: () =>
                                        selectedEventNames.length === uniqueEventNames.length
                                            ? translate(
                                                  `${translationPrefix}.MANAGE_PERMISSIONS_PAGE.ALL_EVENTS_SELECTED`
                                              )
                                            : translate(
                                                  `${translationPrefix}.MANAGE_PERMISSIONS_PAGE.NO_MATCHING_EVENTS`
                                              ),
                                    isSearchable: true,
                                    menuPortalTarget: document.body,
                                }}
                                onChange={handleEventSelect}
                                controlledValue=""
                            />
                            <div className={css.bulkActions}>
                                <button
                                    type="button"
                                    className={css.linkButton}
                                    onClick={handleSelectAll}
                                    disabled={selectedEventNames.length === uniqueEventNames.length}
                                >
                                    {translate(`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.SELECT_ALL_EVENTS`)}
                                </button>
                                <span className={css.separator}>|</span>
                                <button
                                    type="button"
                                    className={css.linkButton}
                                    onClick={handleClearAll}
                                    disabled={selectedEventNames.length === 0}
                                >
                                    {translate(`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.CLEAR_ALL_EVENTS`)}
                                </button>
                            </div>
                        </div>
                        {selectedLabels.length > 0 && (
                            <CollapsedPanelValues
                                values={selectedLabels}
                                itemKeys={selectedEventNames}
                                onDelete={handleRemoveEvent}
                                containerClassName={css.valuesContainer}
                            />
                        )}
                    </>
                )}
            </StepCard>
            <AddFutureItems itemType="sdk_event" form="sdk_event" dataRestriction className={css.addFutureItems} />
        </div>
    );
};

SdkEventStepCard.propTypes = {
    events: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            eventName: PropTypes.string.isRequired,
            displayName: PropTypes.string,
            revenueEvent: PropTypes.bool,
            siteId: PropTypes.number,
            appName: PropTypes.string,
            platform: PropTypes.string,
        })
    ),
    selectedEventNames: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func,
    onToggleAddFuture: PropTypes.func,
    translate: PropTypes.func,
    loading: PropTypes.bool,
    activeMemberType: PropTypes.string,
};

export default SdkEventStepCard;
